import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket, usePoll, fmtTime, missionLabel } from "@/lib/helpers";
import ChatImage from "@/components/ChatImage";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Paperclip, Loader2, Hash, User as UserIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useVocab } from "@/lib/vocab";
import { toast } from "sonner";

const dayLabel = (iso) => {
  const d = new Date(iso), t = new Date();
  const y = new Date(t); y.setDate(t.getDate() - 1);
  if (d.toDateString() === t.toDateString()) return "Aujourd'hui";
  if (d.toDateString() === y.toDateString()) return "Hier";
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long" });
};

export default function Messages() {
  const { user } = useAuth();
  const voc = useVocab();
  const [users, setUsers] = useState([]);
  const [missions, setMissions] = useState([]);
  const [channel, setChannel] = useState("general");
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState({});
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const channelRef = useRef(channel);
  channelRef.current = channel;

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/missions").then((r) => setMissions(r.data)).catch(() => {});
  }, []);

  const loadUnread = useCallback(() => api.get("/messages/unread").then((r) => setUnread(r.data)).catch(() => {}), []);
  const markRead = useCallback((ch) => { api.post("/messages/read", { channel: ch }).then(() => loadUnread()); }, [loadUnread]);
  const load = useCallback(() => { api.get("/messages", { params: { channel } }).then((r) => setMessages(r.data)); }, [channel]);

  useEffect(() => { load(); markRead(channel); }, [load, channel, markRead]);
  useEffect(() => { loadUnread(); }, [loadUnread]);
  useWebSocket((msg) => {
    if (msg.type !== "message") return;
    if (msg.message.channel === channelRef.current) {
      setMessages((m) => m.some((x) => x.id === msg.message.id) ? m : [...m, msg.message]);
      if (msg.message.author_id !== user.id) markRead(channelRef.current);
    } else { loadUnread(); }
  });
  usePoll(() => { load(); loadUnread(); }, 9000);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => { e.preventDefault(); if (!text.trim()) return; await api.post("/messages", { channel, text }); setText(""); };
  const onPickFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seules les images sont acceptées"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/messages", { channel, text: text.trim(), attachment: data.path });
      setText(""); toast.success("Photo envoyée");
    } catch { toast.error("Échec de l'envoi"); } finally { setUploading(false); }
  };

  const channels = [{ id: "general", label: "Équipe générale" },
    ...users.filter((u) => u.id !== user.id).map((u) => ({ id: `dm:${[user.id, u.id].sort().join(":")}`, label: u.name, dm: true }))];
  const missionChannels = missions.slice(0, 30).map((m) => ({ id: `mission:${m.id}`, label: `${missionLabel(m)} · ${m.start?.slice(5, 10)}` }));
  const current = channels.find((c) => c.id === channel);
  const currentLabel = current?.label || (channel.startsWith("mission:") ? `Fil de ${voc.mission}` : channel);

  const Badge = ({ id }) => unread[id] > 0 ? <span className="ml-auto text-[10px] font-bold bg-accent text-accent-foreground rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid={`unread-${id}`}>{unread[id]}</span> : null;

  let lastDay = null;
  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-overline text-accent">Communication interne</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">Messages</h1>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <div className="md:col-span-1 space-y-4">
          <div className="bg-card border border-border rounded-2xl shadow-card p-2">
            <div className="text-xs uppercase tracking-overline text-muted-foreground px-3 py-2">Canaux</div>
            {channels.map((c) => (
              <button key={c.id} onClick={() => setChannel(c.id)} data-testid={`channel-${c.id}`}
                className={cn("w-full text-left px-3 py-2.5 text-sm rounded-xl transition-colors flex items-center gap-2", channel === c.id ? "bg-accent/12 text-foreground font-semibold" : "text-muted-foreground hover:bg-muted")}>
                {c.dm ? <UserIcon className="h-4 w-4" /> : <Hash className="h-4 w-4" />}<span className="truncate">{c.label}</span><Badge id={c.id} />
              </button>
            ))}
          </div>
          {missionChannels.length > 0 && (
            <div className="bg-card border border-border rounded-2xl shadow-card p-3">
              <div className="text-xs uppercase tracking-overline text-muted-foreground px-1 py-1">{`Fils de ${voc.mission}`}</div>
              <Select value={channel.startsWith("mission:") ? channel : ""} onValueChange={setChannel}>
                <SelectTrigger data-testid="mission-channel-select" className="rounded-xl"><SelectValue placeholder="Choisir une mission" /></SelectTrigger>
                <SelectContent>{missionChannels.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}{unread[c.id] ? ` (${unread[c.id]})` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="md:col-span-3 bg-card border border-border rounded-2xl shadow-card overflow-hidden flex flex-col h-[68vh]">
          <div className="px-5 py-3 border-b border-border flex items-center gap-3 glass">
            <div className="h-9 w-9 rounded-full bg-accent/20 grid place-items-center text-sm font-bold uppercase">{currentLabel[0]}</div>
            <div><div className="font-semibold text-sm">{currentLabel}</div><div className="text-[11px] text-muted-foreground">{channel === "general" ? "Toute l'équipe" : channel.startsWith("dm:") ? "Message privé" : `Discussion de ${voc.mission}`}</div></div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-1.5 no-scrollbar chat-bg" data-testid="messages-list">
            {messages.length === 0 && <p className="text-sm text-muted-foreground text-center mt-8">Aucun message. Démarrez la conversation.</p>}
            {messages.map((m) => {
              const mine = m.author_id === user.id;
              const d = dayLabel(m.created_at); const showDay = d !== lastDay; lastDay = d;
              return (
                <React.Fragment key={m.id}>
                  {showDay && <div className="flex justify-center my-3"><span className="text-[11px] bg-background/80 text-muted-foreground px-3 py-1 rounded-full shadow-card">{d}</span></div>}
                  <div className={cn("flex", mine ? "justify-end" : "justify-start")}>
                    <div className={cn("max-w-[78%] px-3 py-2 shadow-card", mine ? "bg-accent text-accent-foreground rounded-2xl rounded-br-md" : "bg-card border border-border rounded-2xl rounded-bl-md")}>
                      {!mine && <div className="text-[11px] font-semibold text-accent mb-0.5">{m.author_name}</div>}
                      {m.attachment && <div className="my-1"><ChatImage path={m.attachment} /></div>}
                      {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
                      <div className={cn("text-[10px] mt-1 text-right", mine ? "text-accent-foreground/70" : "text-muted-foreground")}>{fmtTime(m.created_at)}</div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            <div ref={endRef} />
          </div>

          <form onSubmit={send} className="border-t border-border p-3 flex items-center gap-2 bg-card">
            <input type="file" accept="image/*" ref={fileRef} onChange={onPickFile} className="hidden" data-testid="photo-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} title="Joindre une photo" data-testid="photo-button"
              className="h-10 w-10 shrink-0 rounded-full grid place-items-center text-muted-foreground hover:bg-muted transition-colors">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Paperclip className="h-5 w-5" />}
            </button>
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Votre message…" data-testid="message-input"
              className="flex-1 h-11 px-4 rounded-full bg-muted border-0 outline-none focus:ring-2 focus:ring-accent/40 text-sm" />
            <button type="submit" data-testid="message-send" className="h-11 w-11 shrink-0 rounded-full grid place-items-center bg-accent text-accent-foreground hover:opacity-90 transition-opacity">
              <Send className="h-5 w-5" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
